import { fetchPOI } from 'actions/pois';
import { getLocalization, globalWindow } from 'global/global';
import { useAppDispatch, useAppSelector } from 'index';
import { CHART_DATA_FILTER } from 'Interfaces/ChartInterface';
import { DataPoint } from 'Interfaces/DataPoint';
import * as React from 'react';
import { DropdownItem, FormControl, ListGroup, ListGroupItem, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { Form } from 'react-bootstrap';
import { toast, ToastType } from 'react-toastify';
import { QueryFiltersInterface } from 'reducers/filtersMenuReducer';
import { isNullOrUndefined } from 'utils/utils';

interface Props {
  formId: string;
  dataFilter: CHART_DATA_FILTER;
  selected: string[];
  questions: string[];
  validate?: boolean;
  ListItem: typeof DropdownItem | typeof ListGroupItem;
  onDataPointSelected: (value: string, checked: boolean) => void;
  onDataFilterSelected: (filter: CHART_DATA_FILTER) => void;
}

export default function RadarChartData(props: Props) {

  const dispatch = useAppDispatch();
  const abortController = React.useRef<AbortController>();
  const filtersMenu = useAppSelector(state => state.filtersMenu);
  const [dataPoints, setDataPoints] = React.useState<DataPoint[]>([]);
  const [filtered, setFiltered] = React.useState<DataPoint[]>([]);
  const [filter, setFilter] = React.useState('');
  const [loadingDp, setLoadingDp] = React.useState('');

  React.useEffect(() => {
    if (props.formId) {
      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();
      const filters: QueryFiltersInterface[] = [];
      if (props.dataFilter === 'OWN') {
        filters.push({
          condition : 'IN', field : 'users', value : `${globalWindow.userID}`, operator : '0', fieldOperator : '1'
        });
      }
      const data = dispatch(fetchPOI(
        props.formId, abortController.current.signal, undefined, filters, undefined, 'id,Name'
      ));
      data.then(res => res.json()).then(json => {
        setDataPoints(json);
        setFiltered(json);
      }).catch((e) => {
        console.error(e);
      });
    } else {
      if (abortController.current) {
        abortController.current.abort();
      }
    }
    return () => abortController.current?.abort();
  }, [props.formId, props.dataFilter, filtersMenu]);

  React.useEffect(() => {
    if (filter) {
      setFiltered(dataPoints.map(
        (dp) => `${dp['Name']}`.toLowerCase().search(filter.toLowerCase()) > -1 ? dp : null
      ).filter(dp => dp !== null));
    } else {
      setFiltered(dataPoints);
    }
  }, [filter]);

  const checkDataPoint = (id: string, checked: boolean) => {
    if (checked) {
      setLoadingDp(id);
      const query = {
        id
      };
      const data = dispatch(fetchPOI(
        props.formId, undefined, undefined, undefined, query, ['id'].concat(props.questions).join(',')
      ));
      data.then(res => res.json()).then(json => {
        setLoadingDp('');
        if (json.length) {
          const empty = props.questions.filter((q) => isNullOrUndefined(json[0][q]));
          if (props.questions.length - empty.length < 3) {
            toast(getLocalization('emptyDataPoint'), { type: ToastType.ERROR });
          } else {
            props.onDataPointSelected(id, checked);
          }
        } else if (json.errorCode) {
          toast(json.errorMessage, { type: ToastType.ERROR });
        }
      }).catch(() => {
        console.log('error loading datapoint');
        toast(getLocalization('singleInstanceError'), { type: ToastType.ERROR });
        setLoadingDp('');
      });
    } else {
      props.onDataPointSelected(id, checked);
    }
  };

  const list = React.useCallback(() => {
    const Wrapper = props.ListItem === ListGroupItem ? ListGroup : React.Fragment;
    return (
      <Wrapper>
        {filtered.map((dp, index) => {
          return (
            <props.ListItem as="div" key={`radar-chart-${dp['_id']}-${index}`}>
              <Form.Check
                id={`radar-chart-${dp['_id']}-${index}`}
                type={'checkbox'}
                label={dp['Name']}
                value={dp['id']}
                checked={props.selected.indexOf(`${dp['id']}`) > -1}
                disabled={(
                  props.selected.length === 16 && props.selected.indexOf(`${dp['id']}`) === -1
                ) || loadingDp === dp['id']}
                onChange={(e) => checkDataPoint(e.target.value, e.target.checked)}
              />
            </props.ListItem>
          );
        })}
      </Wrapper>
    );
  }, [filtered, props.selected, props.validate]);

  return (
    <>
      <div className="radar-chart-filter-div">
        <ToggleButtonGroup
          type="radio"
          name="radar-chart-data-filter"
          value={props.dataFilter}
          onChange={(value) => {
            props.onDataFilterSelected(value);
          }}
          size="sm" className="mb-2"
        >
          <ToggleButton
            id={`radar-chart-data-filter-own-btn`}
            type="radio"
            name="radar-chart-data-filter"
            value={'OWN'}
            checked={props.dataFilter === 'OWN'}
          >
            {getLocalization('ownData')}
          </ToggleButton>
          <ToggleButton
            id={`radar-chart-data-filter-all-btn`}
            type="radio"
            name="radar-chart-data-filter"
            value={'ALL'}
            checked={props.dataFilter === 'ALL'}
          >
            {getLocalization('allData')}
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div className="radar-chart-filter-div mb-2">
        <FormControl
          name="filter"
          size="sm"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </div>
      <div className="radar-chart-datapoints">
        {list()}
      </div>
    </>
  );
}
